import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STYLES = ["Blackwork", "Realistisch", "Fineline", "Traditional", "Farbe", "Sonstiges"];

export default function ApplicationSection() {
  const [form, setForm] = useState({ name: "", email: "", studio: "", style: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/apply`, form);
      if (res.data.success) setSuccess(true);
    } catch {
      setError("Fehler beim Senden. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-transparent border-b border-[#5A4A1A] text-[#F0F0F0] py-3 text-sm font-outfit placeholder-[#888888]/60 focus:outline-none focus:border-[#C9A84C] transition-colors duration-300";
  const selectClass = `${inputClass} appearance-none cursor-pointer bg-[#141414]`;

  return (
    <section
      id="bewerbung"
      data-testid="application-section"
      className="py-24 md:py-32 bg-[#060606]"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="section-label text-center block">Karriere</span>
          <h2 className="section-heading text-center">Wir suchen Künstler.</h2>
          <p className="text-[#888888] text-sm md:text-base font-outfit max-w-lg mx-auto">
            Kreativ, handwerklich stark, teamfähig? Werde Teil von Royal Tattoo und
            bringe deine Kunst in unsere Studios.
          </p>
        </motion.div>

        <motion.div
          className="bg-[#141414] border border-[#5A4A1A]/30 p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {success ? (
            <div className="text-center py-12">
              <CheckCircle size={48} className="text-[#C9A84C] mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-cormorant text-3xl italic text-[#F0F0F0] mb-2">
                Bewerbung erhalten!
              </h3>
              <p className="text-[#888888] font-outfit text-sm">
                Danke für dein Interesse. Wir melden uns bald bei dir.
              </p>
            </div>
          ) : (
            <form
              data-testid="application-form"
              onSubmit={handleSubmit}
              className="space-y-8"
            >
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Dein vollständiger Name"
                    className={inputClass}
                    data-testid="apply-name"
                  />
                </div>
                <div>
                  <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="deine@email.de"
                    className={inputClass}
                    data-testid="apply-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">
                    Gewünschter Standort
                  </label>
                  <select
                    name="studio"
                    value={form.studio}
                    onChange={handleChange}
                    className={selectClass}
                    data-testid="apply-studio"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="grevenbroich">Grevenbroich</option>
                    <option value="leverkusen">Leverkusen</option>
                    <option value="beide">Beide Studios</option>
                  </select>
                </div>
                <div>
                  <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">
                    Dein Stil
                  </label>
                  <select
                    name="style"
                    value={form.style}
                    onChange={handleChange}
                    className={selectClass}
                    data-testid="apply-style"
                  >
                    <option value="">Bitte wählen</option>
                    {STYLES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[#888888] text-xs tracking-[0.15em] uppercase font-outfit mb-2 block">
                  Nachricht & Portfolio-Link *
                </label>
                <textarea
                  name="message"
                  required
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Erzähl uns von dir und deiner Arbeit. Portfolio-Link oder Instagram gerne hier angeben."
                  className={`${inputClass} resize-none`}
                  data-testid="apply-message"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm font-outfit" data-testid="apply-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="application-submit"
                className="gold-btn-solid flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="font-outfit text-xs tracking-[0.2em]">Wird gesendet...</span>
                ) : (
                  <>
                    <Send size={15} />
                    <span className="font-outfit text-xs tracking-[0.2em]">Jetzt bewerben</span>
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
